import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { auth, database, analytics } from "./firebase";

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Check if user has already consented
    const initialConsent = localStorage.getItem("cookieConsent");
    if (!initialConsent) {
      setIsVisible(true);
    }

    // Sync consent to DB when user logs in
    const unsubscribe = auth.onAuthStateChanged((user) => {
      const currentConsent = localStorage.getItem("cookieConsent");
      if (user && currentConsent) {
        // Check if we already saved it to avoid writing on every page load
        const ref = database.ref(`CookieConsent/${user.uid}`);
        ref.once("value").then((snapshot) => {
          if (!snapshot.exists() || snapshot.val().status !== currentConsent) {
            ref
              .set({
                status: currentConsent,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
              })
              .catch((err) => console.error("Error syncing consent:", err));
          }
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const saveConsentToDB = async (status) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const timestamp = new Date().toISOString();
        await database.ref(`CookieConsent/${user.uid}`).set({
          status,
          timestamp,
          userAgent: navigator.userAgent,
        });
      }
    } catch (error) {
      console.error("Error saving cookie consent to DB:", error);
    }
  };

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setIsVisible(false);
    saveConsentToDB("accepted");
    // Enable Firebase Analytics
    if (analytics) {
      analytics.setAnalyticsCollectionEnabled(true);
    }
  };

  const handleReject = () => {
    localStorage.setItem("cookieConsent", "rejected");
    setIsVisible(false);
    saveConsentToDB("rejected");
    // Ensure Firebase Analytics remains disabled
    if (analytics) {
      analytics.setAnalyticsCollectionEnabled(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "#111827",
        color: "#f9fafb",
        borderTop: "1px solid #374151",
        boxShadow: "0 -8px 24px rgba(0,0,0,0.35)",
        zIndex: 9999,
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: isMobile ? "16px" : "20px 24px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? "14px" : "20px",
        }}
      >
        {/* Cookie icon + text block */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <span style={{ fontSize: "1.6rem", lineHeight: "1", flexShrink: 0, marginTop: "2px" }}>🍪</span>
          <div>
            <h3
              style={{
                margin: "0 0 5px 0",
                fontSize: isMobile ? "0.95rem" : "1rem",
                fontWeight: "700",
                color: "#f9fafb",
                textAlign: "left",
              }}
            >
              We value your privacy
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "0.82rem",
                lineHeight: "1.6",
                color: "#9ca3af",
                textAlign: "left",
              }}
            >
              We use cookies to enhance your experience and analyse our traffic.
              By clicking <strong style={{ color: "#e5e7eb" }}>Accept All</strong>, you consent to our use of cookies.{" "}
              <Link
                to="/TermsAndConditions"
                style={{ color: "#60a5fa", textDecoration: "underline" }}
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleReject}
            style={{
              padding: "10px 18px",
              backgroundColor: "transparent",
              color: "#9ca3af",
              border: "1px solid #374151",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "0.85rem",
              transition: "border-color 0.2s, color 0.2s",
              width: isMobile ? "100%" : "auto",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "#6b7280";
              e.currentTarget.style.color = "#e5e7eb";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "#374151";
              e.currentTarget.style.color = "#9ca3af";
            }}
          >
            Reject Non-Essential
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: "10px 20px",
              backgroundColor: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "0.85rem",
              boxShadow: "0 2px 8px rgba(37,99,235,0.45)",
              transition: "background-color 0.2s, box-shadow 0.2s",
              width: isMobile ? "100%" : "auto",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#1d4ed8";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.55)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#2563eb";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(37,99,235,0.45)";
            }}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
